import { AddCircleOutlined, DeleteOutlined, EditOutlined, InfoOutlined } from "@mui/icons-material";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Box, Checkbox, IconButton } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../components/InHouseTooltip/InHouseTooltip";
import { SystemRoundedCheckbox } from "../../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "../../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../../hooks/useJobs";
import { hrisMappingSubRoleSortingKey } from "../../../../../services/LocalStorageService";
import type { Job, JobDepartmentSubRole, SubRoleUpsertBody } from "../../../../../utils/models";
import { AgencySource, NurseRole } from "../../../../../utils/models";
import { useDeleteSubRole, useDepartmentSubRoles, useUpsertSubRole } from "./hrisMappingHooks";

// New rows seed the Clipboard source (this grid fixes the Clipboard catalog); the Source dropdown only offers canonical AgencySource values.
const DEFAULT_SUB_ROLE_SOURCE = AgencySource.Clipboard;

export const SubRoleMappingGrid: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = useDepartmentSubRoles();
	const jobs = useJobs();
	const upsertMutation = useUpsertSubRole();
	const deleteMutation = useDeleteSubRole();
	const [dialogOpen, setDialogOpen] = useState(false);
	// When set, the dialog edits this row; when undefined, it adds a new row.
	const [editingRow, setEditingRow] = useState<JobDepartmentSubRole | undefined>(undefined);

	// DEFAULT: useDepartmentSubRoles (useQuery) returns undefined until the first fetch resolves; the grid needs a stable array.
	const rows = useMemo(() => data ?? [], [data]);

	const jobOptions = useMemo(() => jobs.map((j: Job) => ({ value: j.id, label: j.displayName ?? j.name })), [jobs]);
	const roleOptions = useMemo<{ value: NurseRole; label: string }[]>(() => Object.values(NurseRole).map((v) => ({ value: v, label: v })), []);
	const sourceOptions = useMemo(() => Object.values(AgencySource).map((v) => ({ value: v, label: v })), []);

	const sortOptions: MultiSortOption<JobDepartmentSubRole>[] = useMemo(
		() => [
			{ label: t("hris.mapping.column.jobName"), sortColumns: ["jobId"] },
			{ label: t("hris.mapping.subRole.column.subRole"), sortColumns: ["subRole"] },
			{ label: t("hris.mapping.subRole.column.source"), sortColumns: ["source"] },
			{ label: t("hris.mapping.subRole.column.isDefault"), sortColumns: ["isDefault"] },
		],
		[t],
	);

	const upsert = useCallback(
		(body: SubRoleUpsertBody) => {
			track("clipboard_sub_role_save_start", {
				department_id: departmentId,
				job_id: body.jobId,
				sub_role: body.subRole,
				source: body.source,
				// True when an edit moves an existing mapping (key change).
				is_edit: body.replacesRowId !== undefined,
			});
			upsertMutation.mutate(body, {
				onSuccess: () => track("clipboard_sub_role_save_success", { department_id: departmentId, job_id: body.jobId, sub_role: body.subRole }),
				onError: (err) => {
					trackError("clipboard_sub_role_save_error", { department_id: departmentId, error_message: err?.message });
					showErrorSnackbar(t("hris.mapping.saveError"), {}, err);
				},
			});
		},
		[upsertMutation, departmentId, track, trackError, showErrorSnackbar, t],
	);

	const handleDelete = useCallback(
		(row: JobDepartmentSubRole) => {
			track("clipboard_sub_role_delete_start", { department_id: departmentId, row_id: row.id });
			deleteMutation.mutate(row.id, {
				onSuccess: () => track("clipboard_sub_role_delete_success", { department_id: departmentId, row_id: row.id }),
				onError: (err) => {
					trackError("clipboard_sub_role_delete_error", { department_id: departmentId, error_message: err?.message });
					showErrorSnackbar(t("hris.mapping.deleteError"), {}, err);
				},
			});
		},
		[deleteMutation, departmentId, track, trackError, showErrorSnackbar, t],
	);

	const openAddDialog = useCallback(() => {
		setEditingRow(undefined);
		setDialogOpen(true);
	}, []);

	const openEditDialog = useCallback((row: JobDepartmentSubRole) => {
		setEditingRow(row);
		setDialogOpen(true);
	}, []);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
		setEditingRow(undefined);
	}, []);

	// Default toggle keeps the key tuple, so it's a plain update; the backend enforces one default per key and the hook refetches.
	const handleToggleDefault = useCallback(
		(row: JobDepartmentSubRole) => upsert({ jobId: row.jobId, subRole: row.subRole, isDefault: !row.isDefault, source: row.source }),
		[upsert],
	);

	const columns: GridColDef<JobDepartmentSubRole>[] = useMemo(
		() => [
			{
				field: "jobId",
				headerName: t("hris.mapping.column.jobName"),
				flex: 1,
				minWidth: 180,
				// Part of the upsert key — edits go through the dialog, not inline (would orphan the old row).
				editable: false,
				type: "singleSelect",
				valueOptions: jobOptions,
			},
			{
				field: "subRole",
				headerName: t("hris.mapping.subRole.column.subRole"),
				flex: 1,
				minWidth: 140,
				editable: false,
				type: "singleSelect",
				valueOptions: roleOptions,
			},
			{
				field: "source",
				headerName: t("hris.mapping.subRole.column.source"),
				flex: 1,
				minWidth: 140,
				editable: false,
			},
			{
				field: "isDefault",
				headerName: t("hris.mapping.subRole.column.isDefault"),
				width: 110,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) => (
					<Checkbox
						checked={params.row.isDefault}
						onChange={() => handleToggleDefault(params.row)}
						slotProps={{ input: { "aria-label": t("hris.mapping.subRole.column.isDefault") } }}
						data-testid={`hris-mapping-sub-role-default-${params.row.id}`}
					/>
				),
			},
			{
				field: "actions",
				headerName: t("hris.mapping.subRole.column.actions"),
				width: 110,
				align: "center",
				headerAlign: "center",
				display: "flex",
				sortable: false,
				filterable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
						<InHouseTooltip title={t("hris.mapping.subRole.edit")}>
							<IconButton
								size="small"
								aria-label={t("hris.mapping.subRole.edit")}
								onClick={() => openEditDialog(params.row)}
								data-testid={`hris-mapping-sub-role-edit-${params.row.id}`}
							>
								<EditOutlined fontSize="small" />
							</IconButton>
						</InHouseTooltip>
						<InHouseTooltip title={t("hris.mapping.subRole.delete")}>
							<IconButton
								size="small"
								color="error"
								aria-label={t("hris.mapping.subRole.delete")}
								onClick={() => handleDelete(params.row)}
								data-testid={`hris-mapping-sub-role-delete-${params.row.id}`}
							>
								<DeleteOutlined fontSize="small" />
							</IconButton>
						</InHouseTooltip>
					</Box>
				),
			},
		],
		[t, jobOptions, roleOptions, handleToggleDefault, handleDelete, openEditDialog],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
			<SystemDataGrid<JobDepartmentSubRole>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.id}
				sortingOptions={sortOptions}
				defaultSortState={[{ sortColumns: ["jobId"], sortOrder: "asc" }]}
				localStorageKey={hrisMappingSubRoleSortingKey}
				loading={isLoading}
				error={isError}
				containedHeight
				emptyState={
					<EmptyState
						icon={<TableRowsOutlined />}
						title={t("hris.mapping.subRole.empty.title")}
						hint={t("hris.mapping.subRole.empty.hint")}
						action={
							<SystemButton
								buttonVariant="secondary"
								size="small"
								leadingIcon={<AddCircleOutlined fontSize="small" />}
								onClick={openAddDialog}
								data-testid="hris-mapping-sub-role-empty-add"
							>
								{t("hris.mapping.subRole.add")}
							</SystemButton>
						}
					/>
				}
				leadingToolbarNodes={
					<SystemButton
						buttonVariant="secondary"
						leadingIcon={<AddCircleOutlined fontSize="small" />}
						onClick={openAddDialog}
						sx={{ alignSelf: "flex-start" }}
						data-testid="hris-mapping-sub-role-add"
					>
						{t("hris.mapping.subRole.add")}
					</SystemButton>
				}
				trailingToolbarNodes={
					<InHouseTooltip title={t("hris.mapping.subRole.tooltip")}>
						<InfoOutlined />
					</InHouseTooltip>
				}
				dataTestId="hris-mapping-sub-role"
			/>
			<AddOrEditSubRoleDialog
				open={dialogOpen}
				onClose={closeDialog}
				jobOptions={jobOptions}
				roleOptions={roleOptions}
				sourceOptions={sourceOptions}
				editingRow={editingRow}
				onSubmit={(body) => {
					upsert(body);
					closeDialog();
				}}
			/>
		</Box>
	);
};

interface AddOrEditSubRoleDialogProps {
	open: boolean;
	onClose: () => void;
	jobOptions: { value: string; label: string }[];
	roleOptions: { value: NurseRole; label: string }[];
	sourceOptions: { value: AgencySource; label: string }[];
	// When set, the dialog is in edit mode (fields prefilled).
	editingRow?: JobDepartmentSubRole;
	onSubmit: (body: SubRoleUpsertBody) => void;
}

const AddOrEditSubRoleDialog: FC<AddOrEditSubRoleDialogProps> = ({ open, onClose, jobOptions, roleOptions, sourceOptions, editingRow, onSubmit }) => {
	const { t } = useTranslation();
	const isEdit = editingRow !== undefined;
	const [jobId, setJobId] = useState("");
	const [subRole, setSubRole] = useState<NurseRole | "">("");
	const [source, setSource] = useState<AgencySource>(DEFAULT_SUB_ROLE_SOURCE);
	const [isDefault, setIsDefault] = useState(false);
	// Re-seed fields when the dialog opens; keying on open+row id avoids clobbering edits on re-renders.
	const [seededFor, setSeededFor] = useState<string | null>(null);
	const seedKey = open ? (editingRow?.id ?? "add") : null;
	if (seedKey !== seededFor) {
		setSeededFor(seedKey);
		setJobId(editingRow?.jobId ?? "");
		setSubRole(editingRow?.subRole ?? "");
		// Lowercase legacy rows so the value matches the canonical AgencySource dropdown option.
		setSource((editingRow?.source?.toLowerCase() as AgencySource) ?? DEFAULT_SUB_ROLE_SOURCE);
		setIsDefault(editingRow?.isDefault ?? false);
	}

	const canSubmit = jobId !== "" && subRole !== "";

	const handleSubmit = useCallback(() => {
		if (jobId === "" || subRole === "") return;
		onSubmit({
			jobId,
			subRole,
			isDefault,
			source,
			// Edit: backend deletes the original row if the key changed.
			...(isEdit ? { replacesRowId: editingRow.id } : {}),
		});
	}, [jobId, subRole, isDefault, source, isEdit, editingRow, onSubmit]);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={isEdit ? t("hris.mapping.subRole.edit.dialog.title") : t("hris.mapping.subRole.add.dialog.title")}
			dataTestId="hris-mapping-sub-role-add-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
					<SystemDropdown<string>
						fullWidth
						label={t("hris.mapping.column.jobName")}
						items={jobOptions}
						selectedItem={jobId === "" ? undefined : jobId}
						onChange={(value) => setJobId(value ?? "")}
						data-testid="hris-mapping-sub-role-add-job"
					/>
					<SystemDropdown<NurseRole>
						fullWidth
						label={t("hris.mapping.subRole.column.subRole")}
						items={roleOptions}
						selectedItem={subRole === "" ? undefined : subRole}
						onChange={(value) => setSubRole(value ?? "")}
						data-testid="hris-mapping-sub-role-add-role"
					/>
					<SystemDropdown<AgencySource>
						fullWidth
						label={t("hris.mapping.subRole.column.source")}
						items={sourceOptions}
						selectedItem={source}
						onChange={(value) => setSource(value ?? DEFAULT_SUB_ROLE_SOURCE)}
						data-testid="hris-mapping-sub-role-add-source"
					/>
					<SystemRoundedCheckbox
						checked={isDefault}
						onChange={setIsDefault}
						label={t("hris.mapping.subRole.column.isDefault")}
						data-testid="hris-mapping-sub-role-add-default"
					/>
				</Box>
			}
			confirmDisabled={!canSubmit}
			onConfirm={handleSubmit}
		/>
	);
};
